import { useState, useEffect } from "react";

export type VideoEvent = {
	/**
	 * Time in seconds
	 */
	key: string;
	id?: number;
	time: number;
	name: string;
	type: string;
	details: Record<string, any>;
	flag_level: number;
	logged_time: string;
	resolved: boolean;
	comment?: string;
};
export type TimestampsArr = {
	/**
	 * Time in seconds
	 */
	start?: number;
	/**
	 * Time in seconds
	 */
	end?: number;
	vid: VideoEvent["details"][string];
}[];

/**
 * Combine all elements of the array that have the same vid
 *
 * eg: [{vid: 1, start: 1}, {vid: 1, end: 2}] -> [{vid: 1, start: 1, end: 2}]
 */
const consolidateTimestampsArr = (timestamps: TimestampsArr): TimestampsArr => {
	const copiedTimestamps = timestamps.map((timestamp) => ({ ...timestamp }));
	const consolidatedTimestamps: TimestampsArr = [];
	const vidToTimestamp: Map<string, TimestampsArr[number]> = new Map();

	for (const currentTimestamp of copiedTimestamps) {
		const currentVid = String(currentTimestamp.vid);

		const existingTimestamp = vidToTimestamp.get(currentVid);
		if (existingTimestamp) {
			if (currentTimestamp.start && !existingTimestamp.start) {
				existingTimestamp.start = currentTimestamp.start;
			}
			if (currentTimestamp.end && !existingTimestamp.end) {
				existingTimestamp.end = currentTimestamp.end;
			}
		} else {
			vidToTimestamp.set(currentVid, currentTimestamp);
			consolidatedTimestamps.push(currentTimestamp);
		}
	}

	return consolidatedTimestamps;
};

// Does parsing of the timestamps on the front end.
// Could potentially combine this with the timestamp parsing we do on the node-server
export const useTimestamps = (props: { videoEvents: VideoEvent[] }) => {
	const [events, setEvents] = useState(props.videoEvents);
	const [screenTimestampsArr, setScreenTimestampsArr] = useState<TimestampsArr>([]);
	const [webcamTimestampsArr, setWebcamTimestampsArr] = useState<TimestampsArr>([]);

	useEffect(() => {
		let isMicrophone = false;
		const timestampsScreenArr: TimestampsArr = [{ vid: "placeholder" }];
		const timestampsWebcamArr: TimestampsArr = [{ vid: "placeholder" }];
		if (events) {
			// Boolean variable to check if the recording is an "older version"
			// The older version does not support restarting/resetting the recording
			let isOldVersion = false;
			for (const event of events) {
				// The version that supports restarting/resetting the recording is characterized by logging media as "display" instead of "screencapture" like how the
				// old version does it.
				if (event.details?.media === "screencapture") {
					isOldVersion = true;
				} else if (event.details.media === "microphone") {
					isMicrophone = true;
				}
				if (isOldVersion) {
					if (
						event.name === "start recording" &&
						event.details.media === "screencapture" &&
						!timestampsScreenArr?.[1]?.start
					) {
						// This condition is erroneous
						if (timestampsScreenArr?.[1]) {
							timestampsScreenArr[1].start = event.time;
						} else {
							timestampsScreenArr.push({ start: event.time, vid: 0 });
						}
					} else if (
						event.name === "start recording" &&
						event.details.media === "webcam" &&
						!timestampsWebcamArr?.[1]?.start
					) {
						// This condition is erroneous
						if (timestampsWebcamArr?.[1]) {
							timestampsWebcamArr[1].start = event.time;
						} else {
							timestampsWebcamArr.push({ start: event.time, vid: 0 });
						}
					} else if (
						event.name === "end recording" &&
						event.details.media === "screencapture" &&
						!timestampsScreenArr?.[1]?.end
					) {
						if (timestampsScreenArr?.[1]) {
							timestampsScreenArr[1].end = event.time;
						}
						// This condition is erroneous
						else {
							timestampsScreenArr.push({ end: event.time, vid: 0 });
						}
					} else if (
						event.name === "end recording" &&
						event.details.media === "webcam" &&
						!timestampsWebcamArr?.[1]?.end
					) {
						if (timestampsWebcamArr?.[1]) {
							timestampsWebcamArr[1].end = event.time;
						}
						// This condition is erroneous
						else {
							timestampsWebcamArr.push({ end: event.time, vid: 0 });
						}
					}
				} else {
					if (event.name === "start recording" && event.details.success && event.details.media === "display") {
						// This condition is erroneous
						if (
							timestampsScreenArr.length > 1 &&
							timestampsScreenArr[timestampsScreenArr.length - 1]?.vid === event.details.video
						) {
							timestampsScreenArr[timestampsScreenArr.length - 1].start = event.time;
						} else {
							timestampsScreenArr.push({ start: event.time, vid: event.details.video });
						}
					} else if (event.name === "start recording" && event.details.success && event.details.media === "webcam") {
						// This condition is erroneous
						if (
							timestampsWebcamArr.length > 1 &&
							timestampsWebcamArr[timestampsWebcamArr.length - 1]?.vid === event.details.video
						) {
							timestampsWebcamArr[timestampsWebcamArr.length - 1].start = event.time;
						} else {
							timestampsWebcamArr.push({ start: event.time, vid: event.details.video });
						}
					} else if (event.name === "end recording" && event.details.success && event.details.media === "display") {
						if (
							timestampsScreenArr.length > 1 &&
							timestampsScreenArr[timestampsScreenArr.length - 1]?.vid === event.details.video
						) {
							timestampsScreenArr[timestampsScreenArr.length - 1].end = event.time;
						}
						// This condition is erroneous
						else {
							timestampsScreenArr.push({ end: event.time, vid: event.details.video });
						}
					} else if (event.name === "end recording" && event.details.success && event.details.media === "webcam") {
						if (
							timestampsWebcamArr.length > 1 &&
							timestampsWebcamArr[timestampsWebcamArr.length - 1]?.vid === event.details.video
						) {
							timestampsWebcamArr[timestampsWebcamArr.length - 1].end = event.time;
						}
						// This condition is erroneous
						else {
							timestampsWebcamArr.push({ end: event.time, vid: event.details.video });
						}
					}
				}
			}

			if (!timestampsWebcamArr[1] && isMicrophone) {
				for (const event of events) {
					if (isOldVersion) {
						if (
							event.name === "start recording" &&
							event.details.media === "microphone" &&
							!timestampsWebcamArr?.[1] //?.end
						) {
							// This condition is erroneous
							// if (timestampsWebcamArr?.[1]) {
							// 	timestampsWebcamArr[1].start = event.time;
							// } else {
							// 	timestampsWebcamArr.push({ start: event.time, vid: 0 });
							// }
						} else if (
							event.name === "end recording" &&
							event.details.media === "microphone" &&
							!timestampsWebcamArr?.[1] //?.end
						) {
							// if (timestampsWebcamArr?.[1]) {
							// 	timestampsWebcamArr[1].end = event.time;
							// }
							// // This condition is erroneous
							// else {
							// 	timestampsWebcamArr.push({ end: event.time, vid: 0 });
							// }
						}
					} else {
						if (event.name === "start recording" && event.details.success && event.details.media === "microphone") {
							// This condition is erroneous
							if (
								timestampsWebcamArr.length > 1 &&
								timestampsWebcamArr[timestampsWebcamArr.length - 1]?.vid === event.details.video
							) {
								timestampsWebcamArr[timestampsWebcamArr.length - 1].start = event.time;
							} else {
								timestampsWebcamArr.push({ start: event.time, vid: event.details.video });
							}
						} else if (
							event.name === "end recording" &&
							event.details.success &&
							event.details.media === "microphone"
						) {
							if (
								timestampsWebcamArr.length > 1 &&
								timestampsWebcamArr[timestampsWebcamArr.length - 1]?.vid === event.details.video
							) {
								timestampsWebcamArr[timestampsWebcamArr.length - 1].end = event.time;
							}
							// This condition is erroneous
							else {
								timestampsWebcamArr.push({ end: event.time, vid: event.details.video });
							}
						}
					}
				}
			}
		}

		setScreenTimestampsArr(consolidateTimestampsArr(timestampsScreenArr));
		setWebcamTimestampsArr(consolidateTimestampsArr(timestampsWebcamArr));
	}, [events]);

	return {
		screenTimestampsArr: screenTimestampsArr,
		webcamTimestampsArr: webcamTimestampsArr,
		setTimestampEvents: setEvents,
	};
};

export default useTimestamps;
